// Live chat: org-level chat behavior (visitor sessions that create/link
// Contacts and Tickets, message log, optional automatic assistant replies).
// The embed surface is the unified customer widget (ticket intake widget) —
// public chat calls carry the widget's public token, and appearance lives
// per-widget, not here.

import type { SchedulingAvailabilityTemplate } from './scheduling'

export type LiveChatSessionStatus = 'OPEN' | 'CLOSED'

export const LIVE_CHAT_SESSION_STATUSES: readonly LiveChatSessionStatus[] = [
  'OPEN',
  'CLOSED',
] as const

export type LiveChatMessageRole = 'VISITOR' | 'AGENT' | 'ASSISTANT' | 'SYSTEM'

// ---------------------------------------------------------------------------
// Availability + presence
// ---------------------------------------------------------------------------

/**
 * Weekly business-hours template, same shape as the scheduling module's
 * availability ({ mon: [{ start: '09:00', end: '17:00' }], ... } in the
 * config's timezone). Null on the config = always available.
 */
export type LiveChatBusinessHours = SchedulingAvailabilityTemplate

/**
 * Org chat status: DISABLED when the config is off; ONLINE when now is inside
 * business hours AND at least one responder heartbeated recently; else AWAY.
 */
export type LiveChatStatus = 'ONLINE' | 'AWAY' | 'DISABLED'

/** What public surfaces carry (DISABLED renders as no chat block at all) */
export type LiveChatPublicStatus = Exclude<LiveChatStatus, 'DISABLED'>

export interface LiveChatStatusDTO {
  status: LiveChatStatus
}

/**
 * Responder-notification rows ride an existing NotificationType (the DB enum
 * is closed — precedent: SLA events on TICKET_UPDATED) and carry their real
 * identity in data.liveChatEvent, with entityId = the chat session id.
 */
export const LIVE_CHAT_NOTIFICATION_EVENTS = {
  NEW_MESSAGE: 'LIVE_CHAT_NEW_MESSAGE',
} as const

/**
 * Realtime events shared by the authenticated responder app and the public
 * widget stream. Keeping these names here prevents the two chat surfaces from
 * silently drifting apart.
 */
export const LIVE_CHAT_SOCKET_EVENTS = {
  SESSION_CREATED: 'live-chat:session-created',
  SESSION_UPDATED: 'live-chat:session-updated',
  MESSAGE_CREATED: 'live-chat:message-created',
  TYPING: 'live-chat:typing',
} as const

export interface LiveChatTypingDTO {
  sessionId: string
  actor: 'VISITOR' | 'AGENT'
  isTyping: boolean
}

export interface LiveChatSessionChangedDTO {
  sessionId: string
  organizationId: string
}

// ---------------------------------------------------------------------------
// Config (one per org, upserted)
// ---------------------------------------------------------------------------

export interface LiveChatConfigDTO {
  id: string
  organizationId: string
  enabled: boolean
  collectEmail: boolean
  /** Automatic replies: answer visitors with the assistant when enabled */
  aiEnabled: boolean
  /** Org-provided context injected into automatic replies */
  aiSystemHint: string | null
  offlineMessage: string | null
  /**
   * Weekly hours in `timezone`; null = always available. Inline fallback only
   * when availabilityScheduleId is set — the schedule wins.
   */
  businessHours: LiveChatBusinessHours | null
  /** IANA timezone the business hours are expressed in */
  timezone: string | null
  /** Shared org availability schedule; null = custom hours / always on */
  availabilityScheduleId: string | null
  /** Resolved for display — name of the referenced schedule */
  availabilityScheduleName: string | null
  /** Member userIds notified about chats; empty = owners/admins fallback */
  responderUserIds: string[]
  createdAt: string
  updatedAt: string
}

export interface UpdateLiveChatConfigDTO {
  enabled?: boolean
  collectEmail?: boolean
  aiEnabled?: boolean
  aiSystemHint?: string | null
  offlineMessage?: string | null
  businessHours?: LiveChatBusinessHours | null
  timezone?: string | null
  /** Shared org availability schedule; null clears back to custom/always */
  availabilityScheduleId?: string | null
  responderUserIds?: string[]
}

// ---------------------------------------------------------------------------
// Sessions + messages (org inbox)
// ---------------------------------------------------------------------------

export interface LiveChatMessageDTO {
  id: string
  sessionId: string
  role: LiveChatMessageRole
  authorUserId: string | null
  body: string
  createdAt: string
}

export interface LiveChatSessionDTO {
  id: string
  organizationId: string
  visitorKey: string
  visitorName: string | null
  visitorEmail: string | null
  contactId: string | null
  ticketId: string | null
  status: LiveChatSessionStatus
  lastMessageAt: string
  unreadForOrg: number
  /** Truncated body of the most recent message (list rows) */
  lastMessagePreview: string | null
  createdAt: string
  updatedAt: string
}

export interface SendLiveChatAgentReplyDTO {
  body: string
}

// ---------------------------------------------------------------------------
// Public (visitor-facing) widget payloads
// ---------------------------------------------------------------------------

export interface CreateLiveChatPublicSessionDTO {
  /** The embedding widget's public token (TicketIntakeWidget.publicToken) */
  token: string
  visitorKey: string
  name?: string
  email?: string
}

export interface LiveChatPublicSessionDTO {
  sessionId: string
  /** Per-session capability. Required to read or write this conversation. */
  sessionToken: string
  /** Current org availability at session-creation time */
  status: LiveChatPublicStatus
}

export interface SendLiveChatVisitorMessageDTO {
  token: string
  body: string
}

export interface LiveChatPublicMessageDTO {
  id: string
  role: LiveChatMessageRole
  body: string
  createdAt: string
}
